import { z } from 'zod'
import type { BuildPhaseMessage, RightyTurnEvent } from '../types'

export const BuildStageSchema = z.enum([
  'designing',
  'preparing',
  'shaping',
  'revising',
  'reviewing',
  'materializing',
  'executing',
  'verifying',
  'summarizing',
  'completed',
  'stopped',
  'failed'
])
export const BuildPhaseSchema = z.enum(['design', 'review', 'design_revision', 'building'])
export const BuildPhaseStateSchema = z.enum(['streaming', 'complete', 'interrupted', 'failed'])
const CounterSchema = z.number().int().nonnegative()
export const BuildPhaseMessageSchema = z.object({
  id: z.number().int(),
  content: z.string(),
  metadata: z.object({
    rightyTurnId: z.string(),
    buildPhase: z.object({
      key: z.string(),
      phase: BuildPhaseSchema,
      revision: CounterSchema,
      attempt: CounterSchema,
      state: BuildPhaseStateSchema,
      startSequence: CounterSchema
    })
  })
})
export const BuildStageEventSchema = z.object({
  stage: BuildStageSchema,
  unitId: z.string().nullable(),
  label: z.string()
})
export const BuildProgressSchema = z.object({
  step: z.number().int().nonnegative(),
  lines: z.array(z.string())
})
const StartedSchema = z.object({
  messageId: z.number().int(),
  key: z.string(),
  phase: BuildPhaseSchema,
  revision: CounterSchema,
  attempt: CounterSchema
})
const DeltaSchema = z.object({
  messageId: z.number().int(),
  attempt: CounterSchema,
  offset: CounterSchema,
  text: z.string()
})
const FinishedSchema = z.object({
  messageId: z.number().int(),
  attempt: CounterSchema,
  state: z.enum(['complete', 'interrupted', 'failed']),
  content: z.string()
})

/** False means the journal cannot be folded safely without a fresh snapshot. */
export function applyPhaseMessageEvent(messages: BuildPhaseMessage[], event: RightyTurnEvent, turnId: string): boolean {
  if (event.type === 'phase_message_started') {
    const parsed = StartedSchema.safeParse(event.payload)
    if (!parsed.success) {
      return false
    }
    const payload = parsed.data
    const existing = messages.find((message) => message.id === payload.messageId)
    if (existing && existing.metadata.buildPhase.attempt >= payload.attempt) {
      return true
    }
    const message: BuildPhaseMessage = {
      id: payload.messageId,
      content: '',
      metadata: {
        rightyTurnId: turnId,
        buildPhase: {
          key: payload.key,
          phase: payload.phase,
          revision: payload.revision,
          attempt: payload.attempt,
          state: 'streaming',
          startSequence: existing?.metadata.buildPhase.startSequence ?? event.sequence
        }
      }
    }
    if (existing) {
      Object.assign(existing, message)
    } else {
      messages.push(message)
    }
    return true
  }
  if (event.type === 'phase_message_delta') {
    const parsed = DeltaSchema.safeParse(event.payload)
    if (!parsed.success) {
      return false
    }
    const payload = parsed.data
    const message = messages.find((entry) => entry.id === payload.messageId)
    if (!message) {
      return false
    }
    const phase = message.metadata.buildPhase
    if (payload.attempt < phase.attempt) {
      return true
    }
    if (payload.attempt > phase.attempt) {
      return false
    }
    if (phase.state !== 'streaming') {
      return true
    }
    if (payload.offset > message.content.length) {
      return false
    }
    const overlap = Math.min(message.content.length - payload.offset, payload.text.length)
    if (message.content.slice(payload.offset, payload.offset + overlap) !== payload.text.slice(0, overlap)) {
      return false
    }
    message.content += payload.text.slice(overlap)
    return true
  }
  const parsed = FinishedSchema.safeParse(event.payload)
  if (!parsed.success) {
    return false
  }
  const payload = parsed.data
  const message = messages.find((entry) => entry.id === payload.messageId)
  if (!message) {
    return false
  }
  if (payload.attempt < message.metadata.buildPhase.attempt) {
    return true
  }
  if (payload.attempt > message.metadata.buildPhase.attempt) {
    return false
  }
  message.content = payload.content
  message.metadata.buildPhase.state = payload.state
  return true
}
